/**
 * PublicationsManager — handles PublicationsEng.json / PublicationsAlb.json
 * Data shape: { publications: [{ title, link, description, url? }] }
 */
import React, { Component } from 'react';
import AdminLayout from '../AdminLayout.jsx';
import { fetchDataFile, saveDataFile } from '../../../APICall/Api.jsx';

const FILES = { eng: 'PublicationsEng.json', alb: 'PublicationsAlb.json' };
const EMPTY = { title: '', link: '', description: '' };

class PublicationsManager extends Component {
  state = {
    lang: 'eng',
    rawData: null,
    items: [],
    loading: true,
    saving: false,
    error: null,
    modal: null,
    editIndex: null,
    formData: { ...EMPTY },
    savedMsg: '',
  };

  componentDidMount() { this.loadData(); }

  loadData = async () => {
    const { lang } = this.state;
    this.setState({ loading: true, error: null });
    try {
      const data = await fetchDataFile(FILES[lang]);
      this.setState({ rawData: data, items: data.publications || [], loading: false });
    } catch (e) {
      this.setState({ error: 'Failed to load publications.', loading: false });
    }
  };

  switchLang = (lang) => this.setState({ lang, items: [], loading: true, rawData: null }, this.loadData);

  saveData = async (newItems) => {
    this.setState({ saving: true });
    try {
      const { rawData, lang } = this.state;
      const payload = { ...rawData, publications: newItems };
      await saveDataFile(FILES[lang], payload);
      this.setState({ items: newItems, saving: false, savedMsg: 'Saved!' });
      setTimeout(() => this.setState({ savedMsg: '' }), 3000);
    } catch (e) {
      this.setState({ saving: false, error: 'Save failed. Check your admin token.' });
    }
  };

  openAdd  = () => this.setState({ modal: 'add', formData: { ...EMPTY }, editIndex: null });
  openEdit = (idx) => this.setState({ modal: 'edit', editIndex: idx, formData: { ...this.state.items[idx] } });
  closeModal = () => this.setState({ modal: null, formData: { ...EMPTY } });

  handleField = (key, val) =>
    this.setState((s) => ({ formData: { ...s.formData, [key]: val } }));

  handleSave = () => {
    const { items, editIndex, formData, modal } = this.state;
    const newItems = modal === 'add'
      ? [formData, ...items]
      : items.map((it, i) => (i === editIndex ? formData : it));
    this.saveData(newItems);
    this.closeModal();
  };

  handleDelete = (idx) => {
    if (!window.confirm('Delete this publication?')) return;
    const newItems = this.state.items.filter((_, i) => i !== idx);
    this.saveData(newItems);
  };

  render() {
    const { lang, items, loading, saving, modal, formData, savedMsg, error } = this.state;
    const { history, location } = this.props;

    return (
      <AdminLayout title="Manage Publications" history={history} location={location}>
        {/* Toolbar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div className="bgg-lang-tabs">
              {['eng', 'alb'].map((l) => (
                <button key={l} className={`bgg-lang-tab${lang === l ? ' active' : ''}`} onClick={() => this.switchLang(l)}>
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
            {savedMsg && <span style={{ color: '#2e7d32', fontWeight: 600 }}>✓ {savedMsg}</span>}
            {error && <span style={{ color: '#e53935' }}>{error}</span>}
          </div>
          <button className="bgg-btn bgg-btn-primary" onClick={this.openAdd}>
            <i className="fas fa-plus" /> Add Publication
          </button>
        </div>

        {/* Table */}
        <div className="bgg-admin-card">
          <div className="bgg-admin-card-header">
            <h2>Publications ({loading ? '…' : items.length}) — {lang.toUpperCase()}</h2>
          </div>
          {loading
            ? <div style={{ padding: 32, textAlign: 'center', color: '#8a8aaa' }}>Loading…</div>
            : (
              <div style={{ overflowX: 'auto' }}>
                <table className="bgg-admin-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Title</th>
                      <th>Description</th>
                      <th>Link</th>
                      <th style={{ width: 120 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, idx) => (
                      <tr key={idx}>
                        <td style={{ color: '#aaa', width: 40 }}>{idx + 1}</td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <i className="fas fa-file-pdf" style={{ color: '#d32f2f', fontSize: 16 }} />
                            <strong>{item.title}</strong>
                          </div>
                        </td>
                        <td style={{ fontSize: '0.85rem', color: '#555', maxWidth: 280 }}>
                          {(item.description || '').slice(0, 100)}{item.description && item.description.length > 100 ? '…' : ''}
                        </td>
                        <td style={{ fontSize: '0.82rem' }}>
                          {(item.link || item.url)
                            ? <a href={item.link || item.url} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>
                                <i className="fas fa-external-link-alt" /> Open
                              </a>
                            : <span style={{ color: '#ccc' }}>No link</span>}
                        </td>
                        <td>
                          <div className="bgg-table-actions">
                            <button className="bgg-btn bgg-btn-outline bgg-btn-sm" onClick={() => this.openEdit(idx)}>
                              <i className="fas fa-edit" />
                            </button>
                            <button className="bgg-btn bgg-btn-danger bgg-btn-sm" onClick={() => this.handleDelete(idx)}>
                              <i className="fas fa-trash" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {items.length === 0 && (
                  <div style={{ padding: 32, textAlign: 'center', color: '#aaa' }}>
                    No publications yet. Click "Add Publication" to create one.
                  </div>
                )}
              </div>
            )}
        </div>

        {/* Modal */}
        {modal && (
          <div className="bgg-modal-overlay" onClick={this.closeModal}>
            <div className="bgg-modal" onClick={(e) => e.stopPropagation()}>
              <div className="bgg-modal-header">
                <h3>{modal === 'add' ? 'Add Publication' : 'Edit Publication'}</h3>
                <button className="bgg-modal-close" onClick={this.closeModal}>×</button>
              </div>
              <div className="bgg-modal-body">
                <div className="bgg-form-group">
                  <label className="bgg-form-label">Title</label>
                  <input
                    className="bgg-form-input"
                    type="text"
                    value={formData.title || ''}
                    onChange={(e) => this.handleField('title', e.target.value)}
                  />
                </div>
                <div className="bgg-form-group">
                  <label className="bgg-form-label">Download / View Link (URL)</label>
                  <input
                    className="bgg-form-input"
                    type="text"
                    value={formData.link || formData.url || ''}
                    onChange={(e) => this.handleField('link', e.target.value)}
                    placeholder="https://..."
                  />
                </div>
                <div className="bgg-form-group">
                  <label className="bgg-form-label">Description</label>
                  <textarea
                    className="bgg-form-textarea"
                    value={formData.description || ''}
                    onChange={(e) => this.handleField('description', e.target.value)}
                    rows={4}
                  />
                </div>
              </div>
              <div className="bgg-modal-footer">
                <button className="bgg-btn bgg-btn-outline" onClick={this.closeModal}>Cancel</button>
                <button className="bgg-btn bgg-btn-primary" onClick={this.handleSave} disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}
      </AdminLayout>
    );
  }
}

export default PublicationsManager;



// WEBPACK FOOTER //
// ./src/views/Admin/managers/PublicationsManager.jsx